/**
 * Term helpers for the enrolment surfaces.
 *
 * Term dates are zone-less civil `YYYY-MM-DD` strings, inclusive at both ends,
 * and are compared as STRINGS — exact for zero-padded ISO, and no `Date`
 * parsing means no timezone can shift a boundary day. This mirrors the backend
 * (`src/common/utils/enrollment-window.ts`); the two must agree on which term
 * covers a date or the picker would offer a term the server then refuses.
 */

export interface TermOption {
  termId: string;
  name: string;
  startDate: string;
  endDate: string;
}

/** Today as a civil `YYYY-MM-DD` date in the user's local zone. */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The term covering `date`, inclusive of both ends. */
export function coveringTerm<T extends TermOption>(terms: T[], date: string): T | null {
  return terms.find((t) => t.startDate <= date && date <= t.endDate) ?? null;
}

/**
 * Terms a child can still be enrolled into: the one running now plus any that
 * have not started. A term that has already ended is not offered — booking into
 * the past is never what the user meant.
 */
export function bookableTerms<T extends TermOption>(terms: T[], date: string): T[] {
  return terms
    .filter((t) => t.endDate >= date)
    .slice()
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
}

/**
 * Label for one option in the term picker. A future term is disambiguated by
 * when it begins ("Autumn term — starts 1 Sep"), because its name alone does
 * not tell the user they are booking ahead.
 */
export function formatTermOption(term: TermOption, date: string): string {
  if (term.startDate <= date) return term.name;
  return `${term.name} — starts ${formatDayMonth(term.startDate)}`;
}

/** `2026-09-01` -> `1 Sep`. Parsed field-wise to keep it timezone-free. */
export function formatDayMonth(iso: string): string {
  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const [, month, day] = iso.split('-');
  const monthIndex = Number(month) - 1;
  const label = MONTHS[monthIndex];
  if (!label) return iso;
  return `${Number(day)} ${label}`;
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Days of the week (0=Sunday..6=Saturday) as "Mon, Wed", Monday first — the
 * order the week reads in a childcare service. An enrolment with no days
 * stored attends every day the class runs.
 */
export function formatWeekdays(days: number[]): string {
  if (days.length === 0) return 'Every class day';
  return [...days]
    .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
    .map((d) => WEEKDAY_SHORT[d] ?? String(d))
    .join(', ');
}
